import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  TextField,
  List,
  ListItem,
  ListItemText,
  Paper,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import {
  PLACE_AUTOCOMPLETE_DEBOUNCE_MS,
  PLACE_AUTOCOMPLETE_MIN_CHARS,
  PLACE_DETAIL_FIELDS,
} from '../config/placesFields';
import { loadGoogleMaps } from '../utils/googleMapsLoader';
import api from '../config/api';

const CACHE_EXPIRY = 30 * 60 * 1000;
const GOOGLE_PREDICT_TIMEOUT_MS = 2500;

const withTimeout = (promise, ms, label = 'timeout') => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error(label)), ms);
  Promise.resolve(promise).then(
    (value) => {
      clearTimeout(timer);
      resolve(value);
    },
    (error) => {
      clearTimeout(timer);
      reject(error);
    }
  );
});

/**
 * Location search.
 * - free / default → Geoapify (server, already DB-cached)
 * - paid Google Places → Google in the browser; successful hits are stored
 *   server-side for later reuse (write-only on the hot path — never blocks UI)
 */
const LocationAutocomplete = ({
  value,
  onChange,
  onPlaceSelect,
  label = 'Location',
  placeholder = 'Search for a location...',
  required = false,
  disabled = false,
  error = false,
  helperText = '',
  fullWidth = true,
  compact = false,
  variant = 'outlined',
  sx = {},
  provider: providerProp = 'auto',
  tripId = null,
  usePartnerAuth = false,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [menuBox, setMenuBox] = useState(null);
  const [inputValue, setInputValue] = useState(value || '');
  const [resolvedProvider, setResolvedProvider] = useState(
    providerProp === 'auto' ? null : providerProp
  );
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const serviceRef = useRef(null);
  const cacheRef = useRef(new Map());
  const searchSeqRef = useRef(0);
  const authOpts = useMemo(
    () => (usePartnerAuth ? { usePartnerAuth: true } : {}),
    [usePartnerAuth]
  );

  const provider = providerProp === 'auto'
    ? (resolvedProvider || 'geoapify')
    : providerProp;

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    if (providerProp !== 'auto') {
      setResolvedProvider(providerProp);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const params = tripId ? `?tripId=${encodeURIComponent(tripId)}` : '';
        const response = await api.get(`/geo/provider${params}`, authOpts);
        if (!cancelled) {
          setResolvedProvider(response.data?.provider === 'google' ? 'google' : 'geoapify');
        }
      } catch {
        if (!cancelled) setResolvedProvider('geoapify');
      }
    };
    load();
    return () => { cancelled = true; };
  }, [providerProp, tripId, authOpts]);

  useEffect(() => {
    if (provider !== 'google') {
      serviceRef.current = null;
      return undefined;
    }
    let cancelled = false;
    const init = async () => {
      try {
        await loadGoogleMaps();
        if (!cancelled && window.google?.maps?.places?.AutocompleteService) {
          serviceRef.current = new window.google.maps.places.AutocompleteService();
        }
      } catch (err) {
        console.error('Failed to load Google Maps for location search:', err);
      }
    };
    init();
    return () => { cancelled = true; };
  }, [provider]);

  const remember = (prefix, query, results) => {
    const key = `${prefix}:${query.toLowerCase().trim()}`;
    cacheRef.current.set(key, { data: results, timestamp: Date.now() });
    if (cacheRef.current.size > 50) {
      cacheRef.current.delete(cacheRef.current.keys().next().value);
    }
  };

  const readCache = (prefix, query) => {
    const key = `${prefix}:${query.toLowerCase().trim()}`;
    const cached = cacheRef.current.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY) return cached.data;
    return null;
  };

  const searchGeoapify = useCallback(async (query) => {
    const cached = readCache('a', query);
    if (cached) return cached;
    const params = new URLSearchParams({ q: query, provider: 'geoapify' });
    if (tripId) params.set('tripId', String(tripId));
    const response = await api.get(`/geo/autocomplete?${params.toString()}`, authOpts);
    const results = (response.data?.results || []).map((place) => ({
      id: place.placeId || place.address,
      placeId: place.placeId,
      primary: place.name || place.address,
      secondary: place.address !== place.name ? place.address : (place.city || place.country || ''),
      description: place.address || place.name,
      source: 'geoapify',
      coordinates: place.coordinates,
      raw: place,
    }));
    if (results.length) remember('a', query, results);
    return results;
  }, [tripId, authOpts]);

  const storeGoogleCache = useCallback((query, results) => {
    if (!results.length) return;
    api.post('/geo/autocomplete/cache', {
      q: query,
      tripId: tripId || undefined,
      results: results.map((item) => ({
        name: item.primary,
        address: item.description,
        placeId: item.placeId,
        secondary: item.secondary,
        coordinates: item.coordinates,
      })),
    }, authOpts).catch(() => {});
  }, [tripId, authOpts]);

  const ensureGoogleService = useCallback(async () => {
    if (serviceRef.current) return serviceRef.current;
    await withTimeout(loadGoogleMaps(), 4000, 'maps-load-timeout');
    if (!window.google?.maps?.places?.AutocompleteService) {
      throw new Error('Google Places is not available');
    }
    serviceRef.current = new window.google.maps.places.AutocompleteService();
    return serviceRef.current;
  }, []);

  const searchGoogle = useCallback(async (query) => {
    const local = readCache('g', query);
    if (local) return local;

    // Do not GET the shared cache on every keystroke — misses only add latency
    // and log noise. Google runs first; successful hits are POSTed for later.
    const service = await ensureGoogleService();
    const Status = window.google.maps.places.PlacesServiceStatus;
    if (!sessionTokenRef.current && window.google.maps.places.AutocompleteSessionToken) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }

    const results = await withTimeout(new Promise((resolve, reject) => {
      service.getPlacePredictions({
        input: query,
        sessionToken: sessionTokenRef.current || undefined,
      }, (predictions, status) => {
        if (status === Status.OK) {
          resolve((predictions || []).slice(0, 5).map((prediction) => ({
            id: prediction.place_id,
            placeId: prediction.place_id,
            primary: prediction.structured_formatting?.main_text || prediction.description,
            secondary: prediction.structured_formatting?.secondary_text || '',
            description: prediction.description,
            source: 'google',
            coordinates: null,
            raw: prediction,
          })));
          return;
        }
        if (status === Status.ZERO_RESULTS) {
          resolve([]);
          return;
        }
        reject(new Error(status || 'Google autocomplete failed'));
      });
    }), GOOGLE_PREDICT_TIMEOUT_MS, 'google-predict-timeout');

    if (results.length) {
      remember('g', query, results);
      storeGoogleCache(query, results);
    }
    return results;
  }, [storeGoogleCache, ensureGoogleService]);

  const searchPlacesFunction = useCallback(async (query) => {
    const seq = ++searchSeqRef.current;
    try {
      let results = [];
      if (provider === 'google') {
        try {
          results = await searchGoogle(query);
        } catch (err) {
          console.error('Error fetching Google autocomplete:', err);
        }
        if (!results.length) {
          try {
            results = await searchGeoapify(query);
          } catch (err) {
            console.error('Error fetching Geoapify fallback:', err);
          }
        }
      } else {
        try {
          results = await searchGeoapify(query);
        } catch (err) {
          console.error('Error fetching Geoapify autocomplete:', err);
        }
      }
      if (seq !== searchSeqRef.current) return;
      setSuggestions(results);
    } catch (err) {
      console.error('Error fetching location suggestions:', err);
      if (seq === searchSeqRef.current) setSuggestions([]);
    } finally {
      if (seq === searchSeqRef.current) setLoading(false);
    }
  }, [provider, searchGoogle, searchGeoapify]);

  const searchPlaces = useMemo(
    () => debounce(searchPlacesFunction, PLACE_AUTOCOMPLETE_DEBOUNCE_MS),
    [searchPlacesFunction]
  );

  const handleInputChange = (event) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    onChange(newValue);

    if (newValue.trim().length >= PLACE_AUTOCOMPLETE_MIN_CHARS) {
      setLoading(true);
      setShowSuggestions(true);
      searchPlaces(newValue);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setLoading(false);
    }
  };

  const finishSelect = (suggestion, place) => {
    const lat = place?.coordinates?.lat ?? suggestion.coordinates?.lat;
    const lng = place?.coordinates?.lng ?? suggestion.coordinates?.lng;
    const address = place?.address || suggestion.description || suggestion.primary;
    const googleMapsLink = Number.isFinite(lat) && Number.isFinite(lng)
      ? `https://maps.google.com/maps?q=${lat},${lng}&ll=${lat},${lng}&z=16`
      : `https://maps.google.com/maps?q=${encodeURIComponent(address)}`;
    setInputValue(address);
    onChange(address);
    if (onPlaceSelect) {
      onPlaceSelect({
        name: place?.name || suggestion.primary || address,
        address,
        googleMapsLink,
        placeId: place?.placeId || suggestion.placeId || null,
        coordinates: Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null,
        source: suggestion.source || 'geoapify',
      });
    }
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const loadGooglePlace = (placeId) => new Promise((resolve, reject) => {
    const PlacesService = window.google?.maps?.places?.PlacesService;
    if (!PlacesService) {
      reject(new Error('Google Places is not loaded'));
      return;
    }
    const service = new PlacesService(document.createElement('div'));
    service.getDetails({
      placeId,
      fields: PLACE_DETAIL_FIELDS,
      sessionToken: sessionTokenRef.current || undefined,
    }, (place, status) => {
      if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
        reject(new Error(status || 'Place details failed'));
        return;
      }
      const lat = place.geometry?.location?.lat?.();
      const lng = place.geometry?.location?.lng?.();
      resolve({
        name: place.name,
        address: place.formatted_address || place.name,
        placeId: place.place_id || placeId,
        coordinates: Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null,
      });
    });
  });

  const handlePlaceSelect = async (suggestion) => {
    const hasCoords = Number.isFinite(suggestion.coordinates?.lat)
      && Number.isFinite(suggestion.coordinates?.lng);
    if (suggestion.source === 'google' && suggestion.placeId && !hasCoords) {
      try {
        const place = await withTimeout(loadGooglePlace(suggestion.placeId), 4000, 'details-timeout');
        sessionTokenRef.current = null;
        finishSelect(suggestion, place);
        return;
      } catch (err) {
        console.error('Error fetching place details:', err);
      }
    }
    finishSelect(suggestion, suggestion.raw);
  };

  const placeMenu = () => {
    const node = inputRef.current;
    if (!node?.getBoundingClientRect) return;
    const rect = node.getBoundingClientRect();
    setMenuBox({ top: rect.bottom, left: rect.left, width: Math.max(rect.width, 280) });
  };

  useEffect(() => {
    if (!showSuggestions) return undefined;
    placeMenu();
    window.addEventListener('scroll', placeMenu, true);
    window.addEventListener('resize', placeMenu);
    return () => {
      window.removeEventListener('scroll', placeMenu, true);
      window.removeEventListener('resize', placeMenu);
    };
  }, [showSuggestions, suggestions.length]);

  const handleInputFocus = () => {
    if (suggestions.length > 0) setShowSuggestions(true);
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      if (!suggestionsRef.current?.contains(document.activeElement)) {
        setShowSuggestions(false);
      }
    }, 150);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') setShowSuggestions(false);
  };

  const suggestionMenu = showSuggestions && suggestions.length > 0 && menuBox
    ? createPortal(
        <Paper
          ref={suggestionsRef}
          data-testid="location-autocomplete-suggestions"
          onMouseDown={(event) => event.preventDefault()}
          sx={{
            position: 'fixed',
            top: menuBox.top + 4,
            left: menuBox.left,
            width: menuBox.width,
            maxWidth: 420,
            zIndex: 10000,
            maxHeight: 240,
            overflow: 'auto',
          }}
        >
          <List dense sx={{ py: 0 }}>
            {suggestions.map((suggestion) => (
              <ListItem
                key={suggestion.id}
                button
                onClick={() => handlePlaceSelect(suggestion)}
                data-testid={`location-suggestion-${suggestion.id}`}
                sx={{
                  whiteSpace: 'normal',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <LocationIcon color="action" sx={{ mr: 1, flexShrink: 0, alignSelf: 'flex-start', mt: 0.25 }} />
                <ListItemText
                  primary={suggestion.primary}
                  secondary={suggestion.secondary || null}
                  primaryTypographyProps={{ variant: 'body2', noWrap: false }}
                  secondaryTypographyProps={{ variant: 'caption', noWrap: false }}
                  sx={{
                    minWidth: 0,
                    flex: 1,
                    '& .MuiListItemText-primary, & .MuiListItemText-secondary': {
                      overflow: 'visible',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>,
        document.body
      )
    : null;

  return (
    <>
      <Box sx={{ position: 'relative', width: fullWidth ? '100%' : 'auto' }}>
        <TextField
          inputRef={inputRef}
          fullWidth={fullWidth}
          variant={variant}
          label={compact || variant === 'standard' ? undefined : label}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          error={error}
          helperText={helperText}
          size={compact ? 'small' : 'medium'}
          sx={{
            ...(variant === 'standard' ? {
              width: '100%',
              '& .MuiInputBase-root': {
                overflow: 'visible',
                width: '100%',
                pl: 0,
                pr: 0,
              },
              '& .MuiInputBase-input': {
                overflow: 'visible',
                textOverflow: 'clip',
                lineHeight: 1.5,
                pl: 0,
                pr: 0,
                py: 1.25,
                boxSizing: 'content-box',
              },
              '& .MuiInputAdornment-root': { display: 'none' },
            } : {}),
            ...sx,
          }}
          inputProps={{
            'data-testid': 'location-autocomplete-input',
            ...(variant === 'standard' ? { style: { paddingLeft: 0, paddingRight: 0 } } : {}),
          }}
          InputProps={{
            startAdornment: (compact || variant === 'standard')
              ? undefined
              : <LocationIcon color="action" sx={{ mr: 1 }} />,
            endAdornment: loading ? <CircularProgress size={compact ? 16 : 20} /> : undefined,
          }}
        />
      </Box>
      {suggestionMenu}
    </>
  );
};

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default LocationAutocomplete;
